import { router } from '@inertiajs/react'
import { Check, Copy, Globe, Lock, Settings } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/hooks/use-toast'
import { organizationRepositoryPublicViewPath, publicChangelogRepositoryPath } from '@/routes'

type PublicViewSettingsProps = {
  organizationId: string
  isPublicView: boolean
  owner: string
  repo: string
  mutationsEnabled: boolean
}

export function PublicViewSettings({
  organizationId,
  isPublicView,
  owner,
  repo,
  mutationsEnabled,
}: PublicViewSettingsProps) {
  const { toast } = useToast()
  const [open, setOpen] = useState(false)
  const [isPublic, setIsPublic] = useState(isPublicView)
  const [copied, setCopied] = useState(false)
  const [pending, setPending] = useState(false)

  const publicUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}${publicChangelogRepositoryPath(owner, repo)}`
      : publicChangelogRepositoryPath(owner, repo)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl)
      setCopied(true)
      toast({
        description: 'Link copied to clipboard',
        variant: 'success',
      })
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast({
        description: 'Failed to copy link',
        variant: 'destructive',
      })
    }
  }

  const handleToggle = (checked: boolean) => {
    if (!mutationsEnabled) return

    const previous = isPublic
    setIsPublic(checked)
    setPending(true)

    router.patch(
      organizationRepositoryPublicViewPath(organizationId, owner, repo),
      { repository: { is_public_view: checked } },
      {
        preserveScroll: true,
        onFinish: () => setPending(false),
        onError: () => {
          setIsPublic(previous)
          toast({
            description: 'Could not update changelog visibility. Please try again.',
            variant: 'destructive',
          })
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Open public view settings">
          <Settings className="size-5" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Settings</DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="bg-border h-px flex-1" />
              <h3 className="text-lg">Public link</h3>
              <div className="bg-border h-px flex-1" />
            </div>
            <p className="text-muted-foreground text-center text-sm">
              Turn on the Public link to share this changelog with anyone in the world. They won&apos;t need to log in
              and they won&apos;t be able to see anything else in Git Digest.
            </p>
            {!mutationsEnabled ? (
              <p className="text-muted-foreground text-center text-sm">
                Public changelog publishing is read-only until rollout is enabled on this environment.
              </p>
            ) : null}
            <div className="flex items-center justify-center gap-4">
              <Lock className="text-muted-foreground size-5" />
              <Switch
                id="public-changelog"
                checked={isPublic}
                onCheckedChange={handleToggle}
                disabled={!mutationsEnabled || pending}
                aria-label="Toggle public changelog visibility"
              />
              <Globe className="text-muted-foreground size-5" />
            </div>
            {isPublic ? (
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  readOnly
                  value={publicUrl}
                  className="bg-muted font-mono text-sm"
                  aria-label="Public changelog URL"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleCopy}
                  aria-label="Copy to clipboard"
                  className="shrink-0"
                >
                  {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
